
import { useState } from "react";
import { FilmInventory } from "@/types/inventory";
import { FilmCard } from "@/components/inventory/FilmCard";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Layers, ChevronUp, ChevronDown } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";

interface CompatibleFilmsSectionProps {
  isFilmOpen: boolean;
  setIsFilmOpen: (value: boolean) => void;
  compatibleFilms: FilmInventory[];
  cameraFormat: string;
  formatFormat: (format: string) => string;
  onActionComplete: () => void;
}

export function CompatibleFilmsSection({
  isFilmOpen,
  setIsFilmOpen,
  compatibleFilms,
  cameraFormat,
  formatFormat,
  onActionComplete
}: CompatibleFilmsSectionProps) {
  const { t } = useTranslation();

  return (
    <Collapsible
      open={isFilmOpen}
      onOpenChange={setIsFilmOpen}
      className="border rounded-lg overflow-hidden"
    >
      <div className="bg-card p-4 flex items-center justify-between border-b">
        <div>
          <h2 className="text-xl font-semibold">{t('compatible_films')}</h2>
          <p className="text-muted-foreground text-sm">
            {t('film')} that can be used with this camera
          </p>
        </div>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            {isFilmOpen ? 
              <ChevronUp className="h-4 w-4" /> : 
              <ChevronDown className="h-4 w-4" />
            }
          </Button>
        </CollapsibleTrigger>
      </div>
      
      <CollapsibleContent>
        <div className="p-4">
          {compatibleFilms.length === 0 ? (
            <div className="text-center py-12">
              <div className="flex flex-col items-center justify-center space-y-3">
                <Layers className="h-12 w-12 text-muted-foreground opacity-20" />
                <h3 className="font-medium">{t('no_films_found')}</h3>
                <p className="text-sm text-muted-foreground">
                  {`${t('no_films_yet')} ${t('with')} ${formatFormat(cameraFormat)} ${t('format')}`}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {compatibleFilms.map(film => (
                <FilmCard 
                  key={film.id} 
                  film={film} 
                  onActionComplete={onActionComplete} 
                />
              ))}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
